import { skipToken } from '@reduxjs/toolkit/query'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '../../components/button'
import CountCard from '../../components/cards/CountCard'
import { Status } from '../../components/cards/statusTag'
import Icon from '../../components/icon'
import { DateRangePicker } from '../../components/inputs/dateRange'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { ConfirmationModal } from '../../components/modal/app_modals/confirmationModal'
import { SendVouchersModal } from '../../components/modal/app_modals/sendVouchersModal'
import { ViewVoucherModal } from '../../components/modal/app_modals/viewVoucherModal'
import { TabNav } from '../../components/nav/tabNav'
import { TableComponent } from '../../components/table'
import { PageHeader } from '../../components/typography/PageHeader'
import { TrendText } from '../../components/typography/trendText'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import { voucherSourceMap } from '../../constants'
import { formatCurrency, parseDate } from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import {
  useActivateVoucherMutation,
  useDeactivateVoucherMutation,
  useExportVoucherReportMutation,
  useGetVoucherMetricsQuery,
  useGetVouchersQuery,
  useResendVoucherMutation,
} from '../../redux-toolkits/voucher/voucher.slice'
import {
  GetVouchersQuery,
  SingleVoucher,
  VoucherSource,
  VoucherStatus,
} from '../../redux-toolkits/voucher/voucher.type'

type VoucherAction = 'resend' | 'enable' | 'disable' | 'delete'

const voucherActionMap: Record<
  VoucherAction,
  {
    title: string
    subtext: string
    buttonText: string
  }
> = {
  delete: {
    title: 'Confirm Deletion',
    subtext: 'Are you sure you want to delete this voucher?',
    buttonText: 'Delete',
  },
  disable: {
    title: 'Confirm Action',
    subtext: 'Are you sure you want to disable this voucher?',
    buttonText: 'Disable',
  },
  enable: {
    title: 'Confirm Action',
    subtext: 'Are you sure you want to enable this voucher?',
    buttonText: 'Enable',
  },
  resend: {
    title: 'Confirm Action',
    subtext: 'Are you sure you want to resend this voucher?',
    buttonText: 'Resend',
  },
}

export const Vouchers = () => {
  const [query, setQuery] = useState<GetVouchersQuery>()
  const [selected, setSelected] = useState<SingleVoucher>()
  const [actionType, setActionType] = useState<VoucherAction>()

  const [searchParams, setSearchParams] = useSearchParams()

  const { modalOpen, modal, handleModalClose, handleModalOpen, setModalOpen } =
    useModalControl()

  const { data: voucherMetrics, isFetching: fetchingVoucherMetrics } =
    useGetVoucherMetricsQuery(
      query
        ? {
            startDate: query.startDate,
            endDate: query.endDate,
          }
        : skipToken,
      {
        refetchOnMountOrArgChange: true,
      },
    )

  const { data, isFetching } = useGetVouchersQuery(
    query ? { ...query } : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const [resendVoucher, { isLoading: resendingVoucher }] =
    useResendVoucherMutation()

  const [activateVoucher, { isLoading: activatingVoucher }] =
    useActivateVoucherMutation()

  const [deactivateVoucher, { isLoading: deactivatingVoucher }] =
    useDeactivateVoucherMutation()

  const [exportVoucherReport, { isLoading: exportingReport }] =
    useExportVoucherReportMutation()

  const closeModal = () => {
    setActionType(undefined)
    setSelected(undefined)
    handleModalClose()
  }

  useEffect(() => {
    const custDate = searchParams.get('isCustomDateRange')
      ? searchParams.get('isCustomDateRange') === 'true'
      : false

    setQuery({
      page: Number(searchParams.get('page') || 1),
      limit: 20,
      search: searchParams.get('search') || undefined,
      status: (searchParams.get('status') as VoucherStatus) || undefined,
      startDate: custDate
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: custDate ? searchParams.get('endDate') || undefined : undefined,
      source:
        (searchParams.get('source') as VoucherSource) || 'ADMIN_GENERATED',
    })
  }, [searchParams])

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-wrap items-center gap-6 justify-center">
        <PageHeader
          title="Vouchers"
          className="!my-0 flex-1 min-w-fit whitespace-nowrap"
        />

        <Button
          size="medium"
          label="Send Vouchers"
          primary={true}
          onClick={() => {
            handleModalOpen('sendVouchersModal')
          }}
          className="max-md:!w-full"
        />
      </div>

      <TabNav
        activeTab={query?.source ?? 'ADMIN_GENERATED'}
        setActiveTab={(tab) => {
          setSearchParams({
            ...Object.fromEntries(searchParams),
            source: tab as VoucherSource,
          })
        }}
        tabs={Object.keys(voucherSourceMap)
          .filter((i) => ['ADMIN_GENERATED', 'GIFT_CARD_PURCHASE'].includes(i))
          .map((i) => {
            return {
              name: `${voucherSourceMap[i as VoucherSource].text} vouchers`,
              id: i,
            }
          })}
      />

      <div className="flex items-center justify-between flex-wrap gap-4">
        <Dropdown
          menuButton={
            <Button
              label={`${
                query?.startDate
                  ? format(query.startDate, 'dd MMM, yyyy')
                  : 'Start date'
              } - ${
                query?.endDate
                  ? format(query.endDate, 'dd MMM, yyyy')
                  : 'End date'
              }`}
              icon="calendar"
              primary={false}
              className="!font-normal !w-full md:max-w-fit !py-0 !px-0"
              variant="plain"
            />
          }
          menuClassName="!min-w-max !border-[0px]"
          customMenuItems={
            <DateRangePicker
              required
              animate
              navLayout="around"
              mode="range"
              selected={{
                from: parseDate(query?.startDate)?.toDate() ?? undefined,
                to: parseDate(query?.endDate)?.toDate() ?? undefined,
              }}
              onSelect={(selected) => {
                if (selected?.from && selected?.to) {
                  setSearchParams({
                    ...Object.fromEntries(searchParams),
                    startDate: format(selected?.from, 'yyyy-MM-dd'),
                    endDate: format(selected?.to, 'yyyy-MM-dd'),
                    isCustomDateRange: 'true',
                  })
                }
              }}
              numberOfMonths={2}
              disabled={{ after: new Date() }}
            />
          }
          className=""
        />

        <Button
          size="medium"
          label="Export"
          primary={true}
          onClick={async () => {
            exportVoucherReport({
              fileName: 'vouchers.xlsx',
              ...query,
              page: undefined,
              limit: undefined,
              search: query?.search || undefined,
              source: query?.source ?? 'ADMIN_GENERATED',
            })
          }}
          className="max-md:!w-full"
          loading={exportingReport}
        />
      </div>

      <CountCardContainer className="!my-0">
        <CountCard
          count={voucherMetrics?.data?.totalVoucherCount ?? 0}
          text="Total no of vouchers created"
          isCurrency={false}
          loading={fetchingVoucherMetrics}
        >
          <TrendText
            value={
              voucherMetrics?.data?.totalAmountOfVouchersCreatedChange ?? 0
            }
            suffixText={
              query?.startDate && query?.endDate ? '' : 'in the past week'
            }
          />
        </CountCard>

        <CountCard
          count={voucherMetrics?.data?.totalAmountOfVouchersUsedValue ?? 0}
          text="Total amount of voucher used"
          isCurrency
          loading={fetchingVoucherMetrics}
        >
          <TrendText
            value={voucherMetrics?.data?.totalAmountOfVouchersUsedChange ?? 0}
            suffixText={
              query?.startDate && query?.endDate ? '' : 'in the past week'
            }
          />
        </CountCard>

        <CountCard
          count={voucherMetrics?.data?.totalAmountOfVouchersUsed ?? 0}
          text="Used voucher count"
          isCurrency={false}
          valueSuffix="Used"
          loading={fetchingVoucherMetrics}
        >
          <TrendText
            value={voucherMetrics?.data?.totalNumberOfVouchersUsedChange ?? 0}
            suffixText={
              query?.startDate && query?.endDate ? '' : 'in the past week'
            }
          />
        </CountCard>

        <CountCard
          count={voucherMetrics?.data?.expiredVoucherCount ?? 0}
          text="Expired voucher count"
          isCurrency={false}
          valueSuffix="Expired"
          loading={fetchingVoucherMetrics}
        >
          <TrendText
            value={voucherMetrics?.data?.totalExpiredVoucherCountChange ?? 0}
            suffixText={
              query?.startDate && query?.endDate ? '' : 'in the past week'
            }
          />
        </CountCard>
      </CountCardContainer>

      <div className="flex-wrap flex items-center gap-4 justify-between">
        <SearchInput
          placeholder="Search"
          searchFunction={(val) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              search: val,
              page: '1',
            })
          }}
          className="!w-full md:!max-w-[400px]"
        />

        <Dropdown
          menuClassName=""
          menuButton={
            <Button
              label={`${query?.status?.toLowerCase() ?? 'All'}`}
              primary={false}
              icon="filter"
              className="!font-normal ml-auto capitalize"
            />
          }
          menuItems={['ALL', 'ACTIVE', 'REDEEMED', 'DEACTIVATED'].map(
            (filter) => ({
              name: filter?.toLowerCase(),
              value: filter,
              className: 'capitalize',
              onClick: () => {
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  status: filter === 'ALL' ? '' : (filter as VoucherStatus),
                  page: '1',
                })
              },
            }),
          )}
        />
      </div>

      <div className="w-full overflow-x-scroll">
        <TableComponent
          headers={[
            'Voucher Code',
            'Value',
            'Sender',
            'Recipient',
            'Creation date',
            'Usage status',
            'Redemption Date',
            'Actions',
          ]}
          rows={
            data?.data?.docs?.map(({ voucher }) => {
              return {
                id: voucher._id,
                content: [
                  <button
                    key={`${voucher._id}-button`}
                    className=""
                    type="button"
                    onClick={() => {
                      setSelected(voucher)
                      handleModalOpen('viewVoucherDetailsModal')
                    }}
                  >
                    #{voucher.code}
                  </button>,
                  formatCurrency(voucher.value, {
                    decimalPlaces: 2,
                    currency: 'NGN',
                  }),
                  <p
                    key={`${voucher._id}-sender`}
                    className="line-clamp-1 max-w-[150px] text-ellipsis"
                  >
                    {voucher?.metadata?.senderName ?? 'N/A'}
                  </p>,
                  <p
                    key={`${voucher._id}-receiver`}
                    className="line-clamp-1 max-w-[150px] text-ellipsis"
                  >
                    {voucher?.metadata?.recipientName ?? 'N/A'}
                  </p>,
                  format(new Date(voucher.createdAt), 'dd/MM/yyyy h:mma'),
                  voucher.isActive ? (
                    <Status
                      key={`${voucher._id}-status`}
                      text={voucher.used ? 'Redeemed' : 'Unused'}
                      type={voucher.used ? 'success' : 'warn'}
                    />
                  ) : (
                    <Status
                      key={`${voucher._id}-status`}
                      text={'Deactivated'}
                      type={'fail'}
                    />
                  ),
                  voucher?.usedAt
                    ? format(new Date(voucher.usedAt), 'dd/MM/yyyy h:mma')
                    : '-',
                  <Dropdown
                    key={`${voucher._id}-controls`}
                    menuButton={
                      <Icon id="ellipses" height={18} width={18} className="" />
                    }
                    menuItems={[
                      {
                        name: 'View details',
                        value: 'viewDetails',
                        onClick: () => {
                          setSelected(voucher)
                          handleModalOpen('viewVoucherDetailsModal')
                        },
                      },
                      {
                        name: 'Resend',
                        value: 'resend',
                        onClick: () => {
                          setSelected(voucher)
                          setActionType('resend')
                          handleModalOpen('confirmActionModal')
                        },
                        hidden: voucher.used || !voucher.isActive,
                      },
                      {
                        name: 'Enable',
                        value: 'enable',
                        onClick: () => {
                          setSelected(voucher)
                          setActionType('enable')
                          handleModalOpen('confirmActionModal')
                        },
                        hidden: voucher.isActive || voucher.used,
                      },
                      {
                        name: 'Disable',
                        value: 'disable',
                        onClick: () => {
                          setSelected(voucher)
                          setActionType('disable')
                          handleModalOpen('confirmActionModal')
                        },
                        hidden: !voucher.isActive || voucher.used,
                      },
                    ]}
                  />,
                ],
              }
            }) ?? []
          }
          loading={isFetching}
          emptyText="No vouchers found"
          isEmpty={[0, undefined].includes(data?.data?.docs?.length)}
          currentPage={Number(query?.page)}
          totalDataCount={data?.data?.total}
          pageLimit={Number(query?.limit)}
          onPageChange={(page) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              page: `${page}`,
            })
          }}
        />
      </div>

      {selected && actionType ? (
        <ConfirmationModal
          open={modalOpen && modal === 'confirmActionModal'}
          setOpen={setModalOpen}
          message={voucherActionMap[actionType].subtext}
          title={voucherActionMap[actionType].title}
          onCloseCallback={() => {
            closeModal()
          }}
          onConfirm={async () => {
            if (actionType === 'resend') {
              const resp = await resendVoucher({
                voucherId: selected._id,
              })

              if (resp.data?.success) {
                closeModal()
              }
            } else if (actionType === 'enable') {
              const resp = await activateVoucher({
                voucherId: selected._id,
              })

              if (resp.data?.success) {
                closeModal()
              }
            } else if (actionType === 'disable') {
              const resp = await deactivateVoucher({
                voucherId: selected._id,
              })

              if (resp.data?.success) {
                closeModal()
              }
            }
          }}
          loading={resendingVoucher || activatingVoucher || deactivatingVoucher}
          confirmText={voucherActionMap[actionType].buttonText}
        />
      ) : undefined}

      <SendVouchersModal
        open={modalOpen && modal === 'sendVouchersModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={async () => {
          handleModalClose()
        }}
      />

      {selected ? (
        <ViewVoucherModal
          open={modalOpen && modal === 'viewVoucherDetailsModal'}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            closeModal()
          }}
          voucher={selected}
        />
      ) : undefined}
    </div>
  )
}
